<template>
  <b-modal
    v-model="editorShown"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">{{ formTitle }}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity:1 !important"
          @click="$emit('onHidden')"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div class="text-right mt-1 mr-2">
        <b-button @click="$emit('onHidden')" variant="secondary">
          Cancel
        </b-button>
        <b-button
          v-if="!belongsToRecurrence"
          @click="() => submitScheduleEvent('ModifySingleEvent')"
          class="ml-2"
          variant="primary"
        >
          Save
        </b-button>
        <b-button
          v-if="belongsToRecurrence"
          class="ml-2"
          @click="showRecurringEventModal"
          variant="primary"
        >
          Save
        </b-button>
      </div>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Event title"
          :state="formErrors.title.length == 0 ? null : false"
          :invalid-feedback="formErrors.title.join(', ')"
        >
          <b-form-input v-model="form.title" autocomplete="off" />
        </b-form-group>
        <b-form-group
          label="Date, time and duration"
          :state="formErrors.dateTimeDuration.length == 0 ? null : false"
          :invalid-feedback="formErrors.dateTimeDuration.join(', ')"
        >
          <date-time-duration-picker
            v-model="form.dateTimeDuration"
            :dateDisabledFn="datesNonWorking"
          />
        </b-form-group>
        <b-form-group v-if="isZoomEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
              @click="form.scheduleZoomMeeting = !form.scheduleZoomMeeting"
              :value="form.scheduleZoomMeeting"
              height="30"
              width="50"
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
              class="mr-2"
              :disabled="form.users.length == 0"
            />
            <span class="mr-2">Zoom Meeting</span>
            <img :src="require('../../assets/zoom_icon.png')" width="30" />
          </div>
          <div
            v-if="form.users.length == 0"
            class="text-danger mt-2"
            style="font-size:15px"
          >
            Select at least one Participant to attach a Zoom meeting.
          </div>
        </b-form-group>
        <b-form-group v-if="isTeamsEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
              @click="form.scheduleTeamsMeeting = !form.scheduleTeamsMeeting"
              :value="form.scheduleTeamsMeeting"
              height="30"
              width="50"
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
              class="mr-2"
            />
            <span class="mr-2">Teams Meeting</span>
            <img :src="require('../../assets/icons/teams.png')" width="30" />
          </div>
        </b-form-group>
        <b-form-group v-if="isGoogleMeetEnabled" class="mt-2">
          <div class="d-flex align-items-center">
            <vue-toggles
                @click="form.scheduleGoogleMeetMeeting = !form.scheduleGoogleMeetMeeting"
                :value="form.scheduleGoogleMeetMeeting"
                height="30"
                width="50"
                checkedBg="#34C759"
                uncheckedBg="#FF6161"
                class="mr-2"
                :disabled="form.users.length == 0"
            />
            <span class="mr-2">Google Meet Meeting</span>
            <img :src="require('../../assets/google_icon.png')" width="30" />
          </div>
          <div
              v-if="form.users.length == 0"
              class="text-danger mt-2"
              style="font-size:15px"
          >
            Select at least one Participant to attach a Google Meet meeting.
          </div>
        </b-form-group>
        <b-form-group class="mt-2">
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center">
              <vue-toggles
                @click="form.isRecurrent = !form.isRecurrent"
                :value="form.isRecurrent"
                height="30"
                width="50"
                checkedBg="#34C759"
                uncheckedBg="#FF6161"
                :disabled="eventIsInPast"
              />
              <span class="ml-2">Make event recurrent</span>
            </div>
            <div
              v-if="eventIsInPast && !form.isRecurrent"
              class="text-danger mt-2"
              style="font-size:15px;"
            >
              Recurrence is only available on scheduled events in the future.
            </div>
          </div>
        </b-form-group>
        <b-form-group v-show="form.isRecurrent" class="position-relative">
          <div
            class="position-absolute"
            style="inset: -63px 0px -16px -7px; background-color:#ccc;opacity: 0.5;z-index:2;"
            v-if="eventIsInPast"
          ></div>
          <recurrence-control
            v-model="form.recurrence"
            :startDate="startDateTime"
            :formErrors="formErrors.recurrence"
            :maxOccurrences="50"
          />
          <div
            v-if="eventIsInPast && form.isRecurrent"
            class="text-danger"
            style="font-size:15px;margin-bottom: 56px;"
          >
            Recurrence is only available on scheduled events in the future.
          </div>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Participants (users only)"
          :state="formErrors.users.length == 0 ? null : false"
          :invalid-feedback="formErrors.users.join(', ')"
        >
          <multiple-select v-model="form.users" :options="users" />
        </b-form-group>
      </b-col>
    </b-row>
    <b-modal
      title="Edit recurring event"
      header-bg-variant="primary"
      header-text-variant="white"
      size="md"
      no-close-on-backdrop
      centered
      ref="form-modal"
      @ok="e => submitScheduleEvent(selectedOption)"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <div class="text-right mt-1 mr-2">
          <b-button @click="cancel()" variant="secondary">
            Cancel
          </b-button>
          <b-button class="ml-2" @click="ok()" variant="primary">
            Confirm
          </b-button>
        </div>
      </template>
      <b-form-group v-slot="{ ariaDescribedby }">
        <b-form-radio-group
          :options="options"
          v-model="selectedOption"
          :aria-describedby="ariaDescribedby"
          name="some-radios"
          stacked
        ></b-form-radio-group>
      </b-form-group>
    </b-modal>
  </b-modal>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import scheduleEventServer from "../../server/scheduleEvent";
import userServer from "../../server/user";
import locationServer from "../../server/location";

import { validateRequired, validateDate } from "../../helpers/validation";
import MultipleSelect from "../controls/MultipleSelect.vue";
import DateTimeDurationPicker from "../controls/DateTimeDurationPicker.vue";
import RecurrenceControl from "../controls/RecurrenceControl.vue";
import { formatDatePretty } from "../../../../Breeze.Frontend/src/helpers/date";

export default {
  name: "UserEventAddEditModal",
  components: { DateTimeDurationPicker, MultipleSelect, RecurrenceControl },
  props: ["scheduleEventId", "scheduleEventDefaultData", "locationId"],
  data() {
    return {
      users: [],
      form: {
        title: null,
        users: [],
        dateTimeDuration: null,
        scheduleZoomMeeting: false,
        scheduleTeamsMeeting: false,
        scheduleGoogleMeetMeeting: false,
        isRecurrent: false,
        recurrence: {
          frequencyNumber: 1,
          frequencyUnit: "Days",
          monthlyRepeat: null,
          endType: "ByOccurrences",
          endDate: null,
          occurrences: 10,
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false
        }
      },
      formErrors: {
        title: [],
        users: [],
        dateTimeDuration: [],
        recurrence: {
          ends: [],
          weekDay: []
        }
      },
      location: null,
      options: [
        { value: "ModifySingleEvent", text: "This event" },
        {
          value: "ModifyEventAndRecurrence",
          text: "This and following events"
        },
        { value: "ModifyRecurrence", text: "All events" }
      ],
      selectedOption: "ModifySingleEvent"
    };
  },
  async mounted() {
    this.fetchInitialData();
    this.fetchUsers();
    this.location = await locationServer.get(this.locationId);
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    async fetchInitialData() {
      if (this.scheduleEventId) {
        const scheduleEvent = await scheduleEventServer.get(
          this.scheduleEventId
        );
        this.form = {
          title: scheduleEvent.title,
          users: scheduleEvent.users.map(u => ({
            value: u.id,
            label: this.formatUser(u),
            data: u
          })),
          dateTimeDuration: {
            date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
            time: scheduleEvent.allDay
              ? "00:00:00"
              : this.formatTimeOnlyMysql(scheduleEvent.startDateTime),
            duration: scheduleEvent.allDay
              ? null
              : Math.round(
                  moment
                    .duration(
                      moment(scheduleEvent.endDateTime).diff(
                        moment(scheduleEvent.startDateTime)
                      )
                    )
                    .asMinutes()
                ),
            allDay: !!scheduleEvent.allDay
          },
          scheduleZoomMeeting: !!scheduleEvent.zoomMeetingUrl,
          scheduleTeamsMeeting: !!scheduleEvent.teamsMeetingUrl,
          scheduleGoogleMeetMeeting: !!scheduleEvent.googleMeetMeetingUrl,
          zoomMeetingUrl: scheduleEvent.zoomMeetingUrl,
          teamsMeetingUrl: scheduleEvent.teamsMeetingUrl,
          googleMeetMeetingUrl: scheduleEvent.googleMeetMeetingUrl,
          isRecurrent: !!scheduleEvent.recurrence,
          recurrence: scheduleEvent.recurrence
            ? {
                id: scheduleEvent.recurrence?.id,
                frequencyNumber: scheduleEvent.recurrence?.frequencyNumber,
                frequencyUnit: scheduleEvent.recurrence?.frequencyUnit,
                endType: scheduleEvent.recurrence?.endType,
                endDate: scheduleEvent.recurrence?.endDate
                  ? this.formatDateTimeMysql(scheduleEvent.recurrence.endDate)
                  : "",
                occurrences: scheduleEvent.recurrence?.occurrences,
                monthlyRepeat: scheduleEvent.recurrence?.monthlyRepeat,
                sunday: scheduleEvent.recurrence?.sunday,
                monday: scheduleEvent.recurrence?.monday,
                tuesday: scheduleEvent.recurrence?.tuesday,
                wednesday: scheduleEvent.recurrence?.wednesday,
                thursday: scheduleEvent.recurrence?.thursday,
                friday: scheduleEvent.recurrence?.friday,
                saturday: scheduleEvent.recurrence?.saturday
              }
            : {
                frequencyNumber: 1,
                frequencyUnit: "Days",
                monthlyRepeat: null,
                endType: "ByOccurrences",
                endDate: null,
                occurrences: 10,
                sunday: false,
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false
              }
        };
      } else if (this.scheduleEventDefaultData) {
        const scheduleEvent = this.scheduleEventDefaultData;
        this.form.dateTimeDuration = {
          date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
          time: this.formatTimeOnlyMysql(scheduleEvent.startDateTime),
          duration: moment
            .duration(
              moment(scheduleEvent.endDateTime).diff(
                moment(scheduleEvent.startDateTime)
              )
            )
            .asMinutes(),
          allDay: false
        };
      } else {
        this.form.dateTimeDuration = {
          date: null,
          time: null,
          duration: 30,
          allDay: false
        };
      }
    },
    async fetchUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users.map(user => ({
        value: user.id,
        label: this.formatUser(user),
        data: user
      }));
    },
    async showRecurringEventModal() {
      this.$refs["form-modal"].show();
    },
    async submitScheduleEvent(modificationType = "ModifySingleEvent") {
      if (!this.validateScheduleEventSubmit()) {
        return;
      }
      let response = {};
      const formData = {
        title: this.form.title,
        type: "Other",
        users: this.form.users.map(u => u.value),
        allDay: this.form.dateTimeDuration.allDay,
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
        scheduleZoomMeeting: this.form.scheduleZoomMeeting,
        scheduleTeamsMeeting: this.form.scheduleTeamsMeeting,
        scheduleGoogleMeetMeeting: this.form.scheduleGoogleMeetMeeting,
        modification: modificationType,
        ...(this.form.isRecurrent && {
          recurrence: {
            frequencyNumber: this.form.recurrence.frequencyNumber,
            frequencyUnit: this.form.recurrence.frequencyUnit,
            endType: this.form.recurrence.endType,
            ...(this.form.recurrence.endType == "ByDate" && {
              endDate: !this.scheduleEventId
                ? this.formatEndDateSql(this.form.recurrence.endDate)
                : this.form.recurrence.endDate
            }),
            ...(this.form.recurrence.endType == "ByOccurrences" && {
              occurrences: this.form.recurrence.occurrences
            }),
            ...(this.form.recurrence.frequencyUnit == "Months" && {
              monthlyRepeat: this.form.recurrence.monthlyRepeat
            }),
            ...(this.form.recurrence.frequencyUnit == "Weeks" && {
              sunday: this.form.recurrence.sunday,
              monday: this.form.recurrence.monday,
              tuesday: this.form.recurrence.tuesday,
              wednesday: this.form.recurrence.wednesday,
              thursday: this.form.recurrence.thursday,
              friday: this.form.recurrence.friday,
              saturday: this.form.recurrence.saturday
            })
          }
        }),
        locationId: this.location?.id
      };
      if (this.scheduleEventId) {
        try {
          const dialog = await this.$dialog.confirm(
            {
              title: "Do you want to notify participants?"
            },
            {
              view: "multiple-actions",
              options: [
                { value: true, label: "Yes" },
                { value: false, label: "No" }
              ]
            }
          );
          formData.resend = dialog.data.value;

          if (formData.recurrence && formData.recurrence.endDate)
            formData.recurrence.endDate = formData.recurrence.endDate.replace(
              " ",
              "T"
            );

          response = await scheduleEventServer.update(
            this.scheduleEventId,
            formData
          );
        } catch (error) {
          if (error.response.data.date) {
            this.errorMessage = `${
              error.response.data.message
            } ${formatDatePretty(error.response.data.date)}`;
          } else {
            this.errorMessage =
              error.response.data.errorCode === "tooMuchOcurrences"
                ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                : error.response.data.message;
          }
          return;
        }
      } else {
        try {
          response = await scheduleEventServer.create(formData);
        } catch (error) {
          if (error.response.data.date) {
            this.errorMessage = `${
              error.response.data.message
            } ${formatDatePretty(error.response.data.date)}`;
          } else {
            this.errorMessage =
              error.response.data.errorCode === "tooMuchOcurrences"
                ? "The amount of events to generate is too big (max 300). Please modify the parameters and try again."
                : error.response.data.message;
          }
          return;
        }
      }
      if (formData.scheduleZoomMeeting && !response.zoomMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.scheduleEventId ? `updated` : `created`
            } successfully. The user doesn't have an active account on Zoom. For this reason, the zoom meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      if (formData.scheduleTeamsMeeting && !response.teamsMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.scheduleEventId ? `updated` : `created`
            } successfully, but the teams meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      if (formData.scheduleGoogleMeetMeeting && !response.googleMeetMeetingUrl) {
        this.$dialog.alert(
          {
            title: `The event was ${
              this.scheduleEventId ? `updated` : `created`
            } successfully, but the google meet meeting was not attached.`
          },
          {
            view: "info"
          }
        );
      }
      this.showAlertSuccess();
      this.$emit("onModify");
    },
    validateScheduleEventSubmit() {
      let withoutErrors = true;

      this.formErrors.title = [];
      if (!validateRequired(this.form.title)) {
        this.formErrors.title.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.users = [];
      if (!validateRequired(this.form.users)) {
        this.formErrors.users.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.dateTimeDuration = [];
      if (
        !validateRequired(this.form.dateTimeDuration.date) ||
        (!this.form.dateTimeDuration.allDay &&
          (!validateRequired(this.form.dateTimeDuration.time) ||
            !validateRequired(this.form.dateTimeDuration.duration)))
      ) {
        this.formErrors.dateTimeDuration.push("This field is required");
        withoutErrors = false;
      } else if (!validateDate(this.form.dateTimeDuration.date)) {
        this.formErrors.dateTimeDuration.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(
          null,
          new Date(this.form.dateTimeDuration.date + " 00:00:00")
        ) ||
        (!this.form.dateTimeDuration.allDay &&
          ((this.selectedLocation &&
            this.form.dateTimeDuration.time <
              this.selectedLocation.operationStarts) ||
            (this.selectedLocation &&
              this.form.dateTimeDuration.time >
                this.selectedLocation.operationEnds) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time <
                this.location.operationStarts) ||
            (!this.selectedLocation &&
              this.form.dateTimeDuration.time > this.location.operationEnds)))
      ) {
        this.formErrors.dateTimeDuration.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      }

      this.formErrors.recurrence.ends = [];
      if (
        this.form.recurrence.endType == "ByDate" &&
        !validateRequired(this.form.recurrence.endDate)
      ) {
        this.formErrors.recurrence.ends.push(
          "You must specify a date to end the recurrence"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    }
  },
  computed: {
    ...mapGetters("layout", ["datesNonWorking", "getCurrentAdjustedDateTime"]),
    ...mapGetters("client", ["selectedLocation"]),
    ...mapState("layout", {
      isZoomEnabled: state => state.systemSetting.isZoomEnabled,
      isTeamsEnabled: state => state.systemSetting.isTeamsEnabled,
      isGoogleMeetEnabled: state => state.systemSetting.isGoogleMeetEnabled
    }),
    startDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time &&
        !this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = `${this.form.dateTimeDuration.date} ${
        this.form.dateTimeDuration.allDay
          ? "00:00:00"
          : this.form.dateTimeDuration.time
      }`;

      return this.formatDateTimeSql(dateTime);
    },
    endDateTime() {
      if (!this.form.dateTimeDuration) {
        return null;
      }
      if (!this.form.dateTimeDuration.date) {
        return null;
      }
      if (
        !this.form.dateTimeDuration.time ||
        this.form.dateTimeDuration.allDay
      ) {
        return null;
      }

      const dateTime = moment(this.startDateTime).add(
        this.form.dateTimeDuration.duration,
        "minutes"
      );

      return this.formatDateTimeSql(dateTime);
    },
    belongsToRecurrence() {
      return !!this.form.recurrence?.id;
    },
    eventIsInPast() {
      return (
        !this.startDateTime ||
        this.formatDateTimeMysql(this.startDateTime) <=
          this.formatDateTimeMysql(this.getCurrentAdjustedDateTime())
      );
    },
    formTitle() {
      if (this.scheduleEventId) {
        return this.belongsToRecurrence ? "Edit recurrent event" : "Edit event";
      } else {
        return "New event";
      }
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  }
};
</script>

<style></style>
